import { useState, useEffect, useRef, useCallback } from "react";
import { Phone, MapPin, Clock, ChevronDown, ChevronUp, Star, Users, Award, Calendar, Menu, X, Instagram, Facebook, MessageCircle, CheckCircle, Sparkles, Eye, Scissors, Droplets, Syringe, Mail, Shield, Target, Heart, ArrowRight, Camera, ChevronLeft, ChevronRight, Bot, Send, Upload, Loader2, ImageIcon, Minimize2 } from "lucide-react";
import DOMPurify from "dompurify";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d15045e0-58b8-4b88-a331-c2a145de22fb/artifacts/juqq07vp_photo_2022-01-04_23-51-05.jpg";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Convert relative/old-domain image URLs to current domain
const imgUrl = (url) => {
  if (!url) return "";
  // New MongoDB-backed URLs
  if (url.startsWith("/api/website/image/")) return `${API_BASE}${url}`;
  // Legacy disk URLs - convert to new format
  if (url.startsWith("/api/uploads/website/")) {
    const fname = url.replace("/api/uploads/website/", "");
    return `${API_BASE}/api/website/image/${fname}`;
  }
  // Fix old domain URLs
  if (url.includes("/api/uploads/website/")) {
    const fname = url.split("/api/uploads/website/")[1];
    return `${API_BASE}/api/website/image/${fname}`;
  }
  if (url.includes("/api/website/image/")) {
    const fname = url.split("/api/website/image/")[1];
    return `${API_BASE}/api/website/image/${fname}`;
  }
  if (url.startsWith("/api/")) return `${API_BASE}${url}`;
  return url;
};

// ==================== TRANSLATIONS ====================
const translations = {
  ar: {
    nav: { home: "الرئيسية", about: "عن المركز", services: "خدماتنا", gallery: "قبل وبعد", photos: "معرض الصور", hairAnalysis: "تحليل الشعر", offers: "العروض", testimonials: "آراء العملاء", faq: "الأسئلة الشائعة", booking: "حجز موعد", contact: "تواصل معنا" },
    hero: { badge: "خبرة +9 سنوات في زراعة الشعر", title: "المركز الوطني التركي", subtitle: "لزراعة الشعر والتجميل", desc: "نقدم أحدث تقنيات زراعة الشعر بأيدي خبراء متخصصين مع ضمان أفضل النتائج الطبيعية", cta: "احجز استشارتك المجانية", cta2: "WhatsApp", badges: ["نتائج طبيعية", "+9 سنوات خبرة", "+5000 مريض سعيد", "تقنيات حديثة"] },
    about: { tag: "من نحن", title: "المركز الوطني التركي", subtitle: "عن المركز", p1: "المركز الوطني التركي لزراعة الشعر والتجميل هو مركز رائد في العراق متخصص في عمليات زراعة الشعر والتجميل. نحن نستخدم أحدث التقنيات العالمية ونوفر بيئة طبية آمنة ومريحة لجميع مرضانا.", features: [
      { title: "نهج يركز على المريض", desc: "نقيّم احتياجات كل مريض بشكل فردي ونقدم حلولاً مخصصة" },
      { title: "نتائج طبيعية", desc: "نخطط لزراعة الشعر بطريقة تحقق أقصى درجات الطبيعية" },
      { title: "فريق متخصص", desc: "فريقنا من أفضل المتخصصين في مجال زراعة الشعر" }
    ]},
    services: { tag: "خدماتنا", title: "خدمات زراعة الشعر", subtitle: "تقنيات حديثة وفريق متخصص لنتائج طبيعية ودائمة", items: [
      { title: "زراعة الشعر", desc: "زراعة شعر طبيعي بتقنية FUE المتطورة مع نتائج دائمة وطبيعية" },
      { title: "زراعة اللحية", desc: "تكثيف وزراعة شعر اللحية بتقنيات حديثة للحصول على مظهر رجولي جذاب" },
      { title: "زراعة الحاجب", desc: "رسم وزراعة الحاجب بدقة عالية لإطلالة طبيعية ومتناسقة" },
      { title: "جلسة نضارة", desc: "جلسات تجديد ونضارة البشرة باستخدام أحدث التقنيات العلاجية" },
      { title: "بلازما الشعر", desc: "حقن البلازما الغنية بالصفائح الدموية لتقوية بصيلات الشعر" },
      { title: "ميزوثيرابي", desc: "حقن الميزوثيرابي لتغذية فروة الرأس وتحفيز نمو الشعر" }
    ]},
    stats: { items: [{ num: "5000+", label: "عملية ناجحة", desc: "+5000 عملية ناجحة في زراعة الشعر" }, { num: "98%", label: "رضا العملاء", desc: "نسبة رضا عملائنا عن النتائج" }, { num: "9+", label: "سنوات خبرة", desc: "خبرة واسعة في مجال زراعة الشعر" }, { num: "100%", label: "عملية آمنة", desc: "جميع عملياتنا آمنة ومعقمة" }] },
    gallery: { tag: "النتائج", title: "قبل وبعد العملية", subtitle: "شاهد نتائج حقيقية لمرضانا", before: "قبل", after: "بعد" },
    testimonials: { tag: "آراء العملاء", title: "ماذا يقول مرضانا", items: [
      { name: "أحمد محمد", text: "تجربة ممتازة من البداية للنهاية. النتائج فاقت توقعاتي والفريق الطبي محترف جداً.", rating: 5 },
      { name: "علي حسين", text: "أفضل مركز لزراعة الشعر في بغداد. الطاقم ودود ومحترف والنتائج طبيعية جداً.", rating: 5 },
      { name: "محمد عبدالله", text: "خدمة رائعة ومتابعة مستمرة بعد العملية. النتائج بدأت تظهر بشكل واضح.", rating: 5 }
    ]},
    faq: { tag: "الأسئلة الشائعة", title: "أسئلة تهمك", items: [
      { q: "كم تستغرق عملية زراعة الشعر؟", a: "تستغرق العملية عادة من 6 إلى 8 ساعات حسب عدد البصيلات المطلوبة. يتم إجراؤها تحت تخدير موضعي." },
      { q: "متى تظهر النتائج النهائية؟", a: "تبدأ النتائج الأولية بالظهور بعد 3-4 أشهر، والنتائج النهائية خلال 12-18 شهراً." },
      { q: "هل عملية زراعة الشعر مؤلمة؟", a: "لا، العملية تتم تحت تخدير موضعي كامل ولا يشعر المريض بأي ألم." },
      { q: "ما هي تكلفة زراعة الشعر؟", a: "تختلف حسب عدد البصيلات وحالة المريض. ننصح بحجز استشارة مجانية." },
      { q: "هل النتائج دائمة؟", a: "نعم، الشعر المزروع دائم لا يتساقط." },
      { q: "ما هي فترة التعافي؟", a: "يمكن العودة للحياة الطبيعية خلال 2-3 أيام." }
    ]},
    booking: { tag: "حجز موعد", title: "احجز استشارتك المجانية", name: "الاسم الكامل", phone: "رقم الهاتف", service: "الخدمة المطلوبة", selectService: "اختر الخدمة", date: "التاريخ المفضل", notes: "ملاحظات إضافية", submit: "إرسال الحجز", submitting: "جاري الإرسال...", success: "تم إرسال طلب الحجز بنجاح! سنتواصل معك قريباً.", error: "حدث خطأ، يرجى المحاولة مرة أخرى" },
    contact: { tag: "تواصل معنا", title: "معلومات التواصل", subtitle: "تواصل معنا بأي طريقة تناسبك", address: "بغداد، المنصور، مقابل مول بابيلون، عمارة أنوار المنصور (أزياء شكري)، الطابق الخامس", hours: "من 9 صباحاً إلى 8 مساءً", daily: "يومياً", phone_label: "الهاتف", email_label: "البريد", address_label: "العنوان" },
    footer: { rights: "جميع الحقوق محفوظة", brand: "المركز الوطني التركي لزراعة الشعر والتجميل" },
    offers: { tag: "عروض حصرية", title: "عروض وخصومات محدودة", subtitle: "استفد من العروض قبل انتهائها", days: "يوم", hours: "ساعة", min: "دقيقة", sec: "ثانية", claimed: "حجزوا", remaining: "متبقي", bookNow: "احجز العرض", expired: "انتهى العرض", off: "خصم", noOffers: "لا توجد عروض حالياً - تابعنا!" },
    reviews: { tag: "آراء عملائنا", title: "تجارب حقيقية", subtitle: "شوف شنو يقولون عملائنا عن تجربتهم" },
    chat: { title: "المساعد الذكي", subtitle: "أهلاً! كيف أقدر أساعدك؟", placeholder: "اكتب سؤالك هنا...", send: "إرسال", thinking: "جاري التفكير...", welcome: "مرحباً! أنا المساعد الذكي لعيادة Vatan Hair. اسألني أي سؤال عن زراعة الشعر، الخدمات، الأسعار أو احجز موعدك المجاني!", bookLink: "احجز استشارة مجانية", analyzeBtn: "حلل صورتك", error: "حدث خطأ، حاول مرة أخرى", nameLabel: "اسمك الكريم", phonePlaceholder: "رقم هاتفك", startChat: "ابدأ المحادثة", formTitle: "أهلاً بك!", formDesc: "أدخل بياناتك للبدء بالمحادثة" },
    hairAnalysis: { title: "تحليل الشعر بالذكاء الاصطناعي", subtitle: "ارفع صورة لرأسك واحصل على تقييم أولي مجاني", upload: "ارفع صورة", uploadDesc: "اضغط أو اسحب صورة هنا", analyzing: "جاري التحليل بالذكاء الاصطناعي...", result: "نتيجة التحليل", newAnalysis: "تحليل جديد", bookNow: "احجز موعدك الآن", badge: "مجاني", cta: "حلل حالة شعرك مجاناً", ctaDesc: "ارفع صورة واحصل على تقييم فوري من الذكاء الاصطناعي" },
    instagram: { tag: "تابعونا", title: "آخر منشوراتنا", subtitle: "شاهد أحدث الأعمال والنتائج على انستغرام", follow: "تابعنا على انستغرام", likes: "إعجاب" }
  },
  en: {
    nav: { home: "Home", about: "About", services: "Services", gallery: "Before & After", photos: "Gallery", hairAnalysis: "Hair Analysis", offers: "Offers", testimonials: "Testimonials", faq: "FAQ", booking: "Book Now", contact: "Contact" },
    hero: { badge: "9+ Years of Hair Transplant Expertise", title: "Vatan Hair", subtitle: "Hair Transplant & Cosmetics Center", desc: "We offer the latest hair transplant technologies with expert specialists, ensuring the best natural results", cta: "Book Free Consultation", cta2: "WhatsApp", badges: ["Natural Results", "9+ Years Experience", "5000+ Happy Patients", "Modern Technology"] },
    about: { tag: "About Us", title: "Vatan Hair", subtitle: "About the Center", p1: "Vatan Hair - National Turkish Hair Transplant & Cosmetics Center is a leading center in Iraq specializing in hair transplant and cosmetic procedures. We use the latest global technologies and provide a safe and comfortable medical environment.", features: [
      { title: "Patient-Focused Approach", desc: "We individually evaluate each patient's needs and provide customized solutions" },
      { title: "Natural Results", desc: "We plan hair transplants to achieve maximum natural appearance" },
      { title: "Expert Team", desc: "Our team consists of the best specialists in hair transplantation" }
    ]},
    services: { tag: "Our Services", title: "Hair Transplant Services", subtitle: "Modern techniques and specialized team for natural, permanent results", items: [
      { title: "Hair Transplant", desc: "Natural hair transplant using advanced FUE technique with permanent results" },
      { title: "Beard Transplant", desc: "Beard densification with modern techniques for an attractive look" },
      { title: "Eyebrow Transplant", desc: "Precise eyebrow transplant for a natural, symmetrical appearance" },
      { title: "Freshness Session", desc: "Skin renewal sessions using latest therapeutic technologies" },
      { title: "Hair Plasma (PRP)", desc: "Platelet-rich plasma injections to strengthen hair follicles" },
      { title: "Mesotherapy", desc: "Mesotherapy injections to nourish scalp and stimulate hair growth" }
    ]},
    stats: { items: [{ num: "5000+", label: "Successful Operations", desc: "5000+ successful hair transplant operations" }, { num: "98%", label: "Client Satisfaction", desc: "Our client satisfaction rate" }, { num: "9+", label: "Years Experience", desc: "Extensive experience in hair transplantation" }, { num: "100%", label: "Safe Operations", desc: "All our operations are safe and sterile" }] },
    gallery: { tag: "Results", title: "Before & After", subtitle: "See real results from our patients", before: "Before", after: "After" },
    testimonials: { tag: "Testimonials", title: "What Our Patients Say", items: [
      { name: "Ahmed Mohammed", text: "Excellent experience from start to finish. Results exceeded my expectations.", rating: 5 },
      { name: "Ali Hussein", text: "Best hair transplant center in Baghdad. Staff is professional and results are natural.", rating: 5 },
      { name: "Mohammed Abdullah", text: "Great service and continuous follow-up. Results are clearly visible.", rating: 5 }
    ]},
    faq: { tag: "FAQ", title: "Frequently Asked Questions", items: [
      { q: "How long does hair transplant take?", a: "The procedure usually takes 6 to 8 hours depending on grafts needed. Under local anesthesia." },
      { q: "When do final results appear?", a: "Initial results after 3-4 months, full results within 12-18 months." },
      { q: "Is it painful?", a: "No, performed under full local anesthesia. No pain during the operation." },
      { q: "What is the cost?", a: "Varies by grafts needed. Book a free consultation for exact pricing." },
      { q: "Are results permanent?", a: "Yes, transplanted hair is permanent." },
      { q: "What is recovery time?", a: "Return to normal life within 2-3 days." }
    ]},
    booking: { tag: "Book Now", title: "Book Your Free Consultation", name: "Full Name", phone: "Phone Number", service: "Required Service", selectService: "Select Service", date: "Preferred Date", notes: "Additional Notes", submit: "Submit Booking", submitting: "Submitting...", success: "Booking sent successfully! We'll contact you soon.", error: "An error occurred, please try again" },
    contact: { tag: "Contact Us", title: "Contact Information", subtitle: "Reach us in any way that suits you", address: "Baghdad, Al-Mansour, opposite Babylon Mall, Anwar Al-Mansour Building, 5th Floor", hours: "9:00 AM to 8:00 PM", daily: "Daily", phone_label: "Phone", email_label: "Email", address_label: "Address" },
    footer: { rights: "All Rights Reserved", brand: "Vatan Hair - National Turkish Hair Transplant & Cosmetics Center" },
    offers: { tag: "Exclusive Offers", title: "Limited Time Deals", subtitle: "Don't miss out on these special offers", days: "Days", hours: "Hours", min: "Min", sec: "Sec", claimed: "Claimed", remaining: "Left", bookNow: "Book Now", expired: "Expired", off: "OFF", noOffers: "No offers currently - follow us!" },
    reviews: { tag: "Client Reviews", title: "Real Experiences", subtitle: "See what our clients say about their experience" },
    chat: { title: "AI Assistant", subtitle: "Hello! How can I help you?", placeholder: "Type your question...", send: "Send", thinking: "Thinking...", welcome: "Hello! I'm Vatan Hair's AI assistant. Ask me anything about hair transplant, services, prices, or book a free consultation!", bookLink: "Book Free Consultation", analyzeBtn: "Analyze Your Photo", error: "An error occurred, try again", nameLabel: "Your Name", phonePlaceholder: "Phone Number", startChat: "Start Chat", formTitle: "Welcome!", formDesc: "Enter your info to start chatting" },
    hairAnalysis: { title: "AI Hair Analysis", subtitle: "Upload a photo and get a free initial assessment", upload: "Upload Photo", uploadDesc: "Click or drag a photo here", analyzing: "AI is analyzing...", result: "Analysis Result", newAnalysis: "New Analysis", bookNow: "Book Now", badge: "FREE", cta: "Analyze your hair condition for free", ctaDesc: "Upload a photo and get an instant AI assessment" },
    instagram: { tag: "Follow Us", title: "Our Latest Posts", subtitle: "See our latest work and results on Instagram", follow: "Follow us on Instagram", likes: "likes" }
  }
};

const serviceIcons = [Scissors, Scissors, Eye, Sparkles, Droplets, Syringe];
const aboutIcons = [Heart, Target, Shield];
const statIcons = [Users, Star, Clock, CheckCircle];

// ==================== BEFORE/AFTER SLIDER ====================
const BeforeAfterSlider = ({ before, after, labelBefore, labelAfter }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, x)));
  }, []);

  const onMouseDown = () => { dragging.current = true; };
  const onMouseUp = () => { dragging.current = false; };
  const onMouseMove = (e) => { if (dragging.current) handleMove(e.clientX); };
  const onTouchMove = (e) => { handleMove(e.touches[0].clientX); };

  useEffect(() => {
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => { window.removeEventListener("mouseup", onMouseUp); window.removeEventListener("mousemove", onMouseMove); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBefore = before && before.length > 0;
  const hasAfter = after && after.length > 0;

  return (
    <div ref={containerRef} className="relative w-full h-72 sm:h-80 rounded-2xl overflow-hidden cursor-col-resize select-none bg-gray-800" onTouchMove={onTouchMove} data-testid="before-after-slider">
      {/* After image (full) */}
      <div className="absolute inset-0">
        {hasAfter ? <img src={imgUrl(after)} alt="After" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Users className="w-16 h-16 text-gray-600" /></div>}
      </div>
      {/* Before image (clipped) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        {hasBefore ? <img src={imgUrl(before)} alt="Before" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-700 flex items-center justify-center"><Users className="w-16 h-16 text-gray-500" /></div>}
      </div>
      {/* Slider line */}
      <div className="absolute top-0 bottom-0 w-0.5 bg-amber-400 z-10" style={{ left: `${pos}%` }}>
        <div onMouseDown={onMouseDown} onTouchStart={onMouseDown} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing">
          <div className="flex gap-0.5">
            <div className="w-0.5 h-4 bg-gray-900 rounded-full" />
            <div className="w-0.5 h-4 bg-gray-900 rounded-full" />
          </div>
        </div>
      </div>
      {/* Labels */}
      <div className="absolute top-3 left-3 px-3 py-1 bg-red-500/90 text-white text-xs font-bold rounded-full z-10">{labelBefore}</div>
      <div className="absolute top-3 right-3 px-3 py-1 bg-amber-400/90 text-gray-900 text-xs font-bold rounded-full z-10">{labelAfter}</div>
    </div>
  );
};

// ==================== AI CHAT WIDGET ====================
const ChatWidget = ({ lang, cms }) => {
  const t = translations[lang];
  const isRTL = lang === "ar";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && registered && messages.length === 0) {
      setMessages([{ role: "assistant", text: t.chat.welcome }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registered]);

  const registerLead = async (source = "chatbot") => {
    if (!visitorName.trim() || !visitorPhone.trim()) return;
    setRegistering(true);
    try {
      await fetch(`${API_BASE}/api/leads/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: visitorName, phone: visitorPhone, session_id: sessionId, source })
      });
      setRegistered(true);
      if (source === "chatbot") {
        setMessages([{ role: "assistant", text: `${isRTL ? `أهلاً ${visitorName}! ` : `Hello ${visitorName}! `}${t.chat.welcome}` }]);
      }
    } catch (err) { console.error("Lead registration error:", err); setRegistered(true); }
    finally { setRegistering(false); }
  };

  const sendMessage = async (quickText) => {
    const text = (quickText || input).trim();
    if (!text || loading) return;
    if (!quickText) setInput("");
    setMessages(prev => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/chatbot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId, visitor_name: visitorName, visitor_phone: visitorPhone })
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", text: t.chat.error }]);
      }
    } catch (err) {
      console.error("Chat send error:", err);
      setMessages(prev => [...prev, { role: "assistant", text: t.chat.error }]);
    } finally { setLoading(false); }
  };

  const formatMsg = (text) => {
    return text.split('\n').map((line, i) => {
      let formatted = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted) }} />;
    });
  };

  if (showAnalysis) return <HairAnalysisWidget lang={lang} onClose={() => setShowAnalysis(false)} sessionId={sessionId} cms={cms} visitorName={visitorName} visitorPhone={visitorPhone} registered={registered} registerLead={registerLead} />;

  return (
    <>
      {/* Chat Toggle Button */}
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-xl shadow-amber-400/30 hover:scale-110 transition-all group" data-testid="chat-toggle-btn">
          <Bot className="w-8 h-8 text-gray-900" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-[#0a0f1a] animate-pulse" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className={`fixed bottom-4 z-50 w-[92vw] sm:w-[400px] max-h-[85vh] flex flex-col bg-[#0d1220] border border-white/10 rounded-2xl shadow-2xl overflow-hidden ${isRTL ? 'left-4 sm:left-auto sm:right-4' : 'right-4'}`} dir={isRTL ? "rtl" : "ltr"} style={{ fontFamily: isRTL ? "'Tajawal',sans-serif" : "'Inter',sans-serif" }} data-testid="chat-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-400 to-amber-500 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gray-900/20 rounded-full flex items-center justify-center"><Bot className="w-5 h-5 text-gray-900" /></div>
              <div>
                <h4 className="text-gray-900 font-bold text-sm">{t.chat.title}</h4>
                <p className="text-gray-800 text-xs">{registered ? visitorName : t.chat.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {registered && <button onClick={() => setShowAnalysis(true)} className="p-1.5 hover:bg-black/10 rounded-lg transition-colors" title={t.chat.analyzeBtn} data-testid="chat-analyze-btn"><Camera className="w-5 h-5 text-gray-900" /></button>}
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-black/10 rounded-lg transition-colors" data-testid="chat-close-btn"><Minimize2 className="w-5 h-5 text-gray-900" /></button>
            </div>
          </div>

          {!registered ? (
            /* Registration Form */
            <div className="p-6 space-y-4 min-h-[300px] flex flex-col justify-center" data-testid="chat-register-form">
              <div className="text-center mb-2">
                <div className="w-16 h-16 mx-auto bg-amber-400/10 rounded-full flex items-center justify-center mb-3"><Bot className="w-8 h-8 text-amber-400" /></div>
                <h3 className="text-white font-bold text-lg">{t.chat.formTitle}</h3>
                <p className="text-gray-400 text-sm mt-1">{t.chat.formDesc}</p>
              </div>
              <div>
                <input value={visitorName} onChange={e => setVisitorName(e.target.value)} placeholder={t.chat.nameLabel} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:border-amber-400 focus:outline-none" data-testid="register-name" />
              </div>
              <div>
                <input value={visitorPhone} onChange={e => setVisitorPhone(e.target.value)} placeholder={t.chat.phonePlaceholder} type="tel" dir="ltr" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:border-amber-400 focus:outline-none text-left" data-testid="register-phone" />
              </div>
              <button onClick={() => registerLead("chatbot")} disabled={registering || !visitorName.trim() || !visitorPhone.trim()} className="w-full py-3 bg-amber-400 text-gray-900 font-bold rounded-xl hover:bg-amber-300 disabled:opacity-40 transition-all flex items-center justify-center gap-2" data-testid="register-submit">
                {registering ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {t.chat.startChat}
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[50vh] scrollbar-hide" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? (isRTL ? "justify-start" : "justify-end") : (isRTL ? "justify-end" : "justify-start")}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-amber-400 text-gray-900 rounded-bl-sm"
                    : "bg-white/5 border border-white/10 text-gray-200 rounded-br-sm"
                }`} data-testid={`chat-msg-${i}`}>
                  {msg.role === "assistant" ? formatMsg(msg.text) : msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className={`flex ${isRTL ? "justify-end" : "justify-start"}`}>
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 rounded-br-sm">
                  <div className="flex items-center gap-2 text-amber-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{t.chat.thinking}</div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 flex gap-2 shrink-0 overflow-x-auto scrollbar-hide">
            <button onClick={() => sendMessage(isRTL ? "كم تكلفة زراعة الشعر؟" : "Hair transplant cost?")} className="shrink-0 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-full text-gray-400 hover:text-amber-400 hover:border-amber-400/30 transition-colors" data-testid="quick-price">{isRTL ? "الأسعار" : "Prices"}</button>
            <button onClick={() => sendMessage(isRTL ? "ما هي التقنيات المتاحة؟" : "What techniques available?")} className="shrink-0 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-full text-gray-400 hover:text-amber-400 hover:border-amber-400/30 transition-colors" data-testid="quick-techniques">{isRTL ? "التقنيات" : "Techniques"}</button>
            <button onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })} className="shrink-0 px-3 py-1.5 text-xs bg-amber-400/10 border border-amber-400/30 rounded-full text-amber-400 hover:bg-amber-400/20 transition-colors" data-testid="quick-book">{t.chat.bookLink}</button>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10 shrink-0">
            <div className="flex gap-2">
              <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder={t.chat.placeholder} className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none" data-testid="chat-input" />
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="px-4 py-2.5 bg-amber-400 text-gray-900 rounded-xl hover:bg-amber-300 disabled:opacity-40 transition-all" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// ==================== HAIR ANALYSIS WIDGET ====================
const HairAnalysisWidget = ({ lang, onClose, sessionId, cms, visitorName, visitorPhone, registered, registerLead }) => {
  const t = translations[lang];
  const isRTL = lang === "ar";
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [localName, setLocalName] = useState(visitorName || "");
  const [localPhone, setLocalPhone] = useState(visitorPhone || "");
  const [localRegistering, setLocalRegistering] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.match(/image\/(jpeg|png|webp)/)) {
      setError(isRTL ? "الصيغ المدعومة: JPG, PNG, WEBP" : "Supported: JPG, PNG, WEBP");
      return;
    }
    setError(null);
    setImagePreview(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const doLocalRegister = async () => {
    if (!localName.trim() || !localPhone.trim()) return;
    setLocalRegistering(true);
    try {
      await fetch(`${API_BASE}/api/leads/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: localName, phone: localPhone, session_id: sessionId, source: "hair_analysis" })
      });
      if (registerLead) registerLead("hair_analysis");
    } catch (err) { console.error("Analysis image error:", err); }
    finally { setLocalRegistering(false); }
  };

  const isRegistered = registered || false;

  const analyze = async () => {
    if (!image || loading) return;
    if (!isRegistered && (!localName.trim() || !localPhone.trim())) return;
    if (!isRegistered) await doLocalRegister();
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/hair-analysis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: image, session_id: sessionId, visitor_name: localName || visitorName, visitor_phone: localPhone || visitorPhone })
      });
      const data = await res.json();
      if (res.ok) setResult(data.analysis);
      else setError(data.detail || t.chat.error);
    } catch (err) { console.error("Analysis error:", err); setError(t.chat.error); }
    finally { setLoading(false); }
  };

  const reset = () => { setImage(null); setImagePreview(null); setResult(null); setError(null); };

  const formatAnalysis = (text) => {
    return text.split('\n').map((line, i) => {
      let formatted = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted) }} />;
    });
  };

  return (
    <div className={`fixed bottom-4 z-50 w-[92vw] sm:w-[400px] max-h-[85vh] flex flex-col bg-[#0d1220] border border-white/10 rounded-2xl shadow-2xl overflow-hidden ${isRTL ? 'left-4 sm:left-auto sm:right-4' : 'right-4'}`} dir={isRTL ? "rtl" : "ltr"} style={{ fontFamily: isRTL ? "'Tajawal',sans-serif" : "'Inter',sans-serif" }} data-testid="hair-analysis-window">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center"><Camera className="w-5 h-5 text-white" /></div>
          <div>
            <h4 className="text-white font-bold text-sm">{t.hairAnalysis.title}</h4>
            <p className="text-white/70 text-xs">{t.hairAnalysis.subtitle}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="analysis-close-btn"><X className="w-5 h-5 text-white" /></button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[60vh]">
        {!result ? (
          <>
            {/* Upload Area */}
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${imagePreview ? 'border-emerald-400/50 bg-emerald-400/5' : 'border-white/20 hover:border-emerald-400/50'}`}
              data-testid="analysis-upload-area"
            >
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={e => handleFile(e.target.files[0])} className="hidden" data-testid="analysis-file-input" />
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
              ) : (
                <div className="space-y-3">
                  <div className="w-16 h-16 mx-auto bg-emerald-400/10 rounded-full flex items-center justify-center"><Upload className="w-8 h-8 text-emerald-400" /></div>
                  <p className="text-gray-400 text-sm">{t.hairAnalysis.uploadDesc}</p>
                  <p className="text-gray-600 text-xs">JPG, PNG, WEBP</p>
                </div>
              )}
            </div>

            {error && <p className="text-red-400 text-sm text-center" data-testid="analysis-error">{error}</p>}

            {/* Name/Phone if not registered */}
            {!isRegistered && (
              <div className="space-y-2">
                <input value={localName} onChange={e => setLocalName(e.target.value)} placeholder={t.chat.nameLabel} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:border-emerald-400 focus:outline-none" data-testid="analysis-name" />
                <input value={localPhone} onChange={e => setLocalPhone(e.target.value)} placeholder={t.chat.phonePlaceholder} type="tel" dir="ltr" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:border-emerald-400 focus:outline-none text-left" data-testid="analysis-phone" />
              </div>
            )}

            {/* Analyze Button */}
            <button onClick={analyze} disabled={!image || loading || (!isRegistered && (!localName.trim() || !localPhone.trim()))} className="w-full py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2" data-testid="analysis-submit-btn">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" />{t.hairAnalysis.analyzing}</> : <><Camera className="w-5 h-5" />{t.hairAnalysis.upload}</>}
            </button>
          </>
        ) : (
          <>
            {/* Result */}
            <div className="space-y-3">
              {imagePreview && <img src={imagePreview} alt="" className="w-full h-40 object-cover rounded-xl" />}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h4 className="text-emerald-400 font-bold text-sm mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{t.hairAnalysis.result}</h4>
                <div className="text-gray-300 text-sm leading-relaxed" data-testid="analysis-result">
                  {formatAnalysis(result)}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={reset} className="flex-1 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-xl hover:bg-white/10 transition-colors text-sm" data-testid="analysis-new-btn">{t.hairAnalysis.newAnalysis}</button>
                <button onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })} className="flex-1 py-3 bg-amber-400 text-gray-900 font-bold rounded-xl hover:bg-amber-300 transition-all text-sm" data-testid="analysis-book-btn">{t.hairAnalysis.bookNow}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== OFFER CARD WITH COUNTDOWN ====================
const OfferCard = ({ offer, lang, t }) => {
  const isRTL = lang === "ar";
  const [timeLeft, setTimeLeft] = useState({});
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!offer.end_date) return;
    const timer = setInterval(() => {
      const diff = new Date(offer.end_date) - new Date();
      if (diff <= 0) { setExpired(true); clearInterval(timer); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        min: Math.floor((diff / (1000 * 60)) % 60),
        sec: Math.floor((diff / 1000) % 60)
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [offer.end_date]);

  const title = isRTL ? offer.title_ar : offer.title_en;
  const desc = isRTL ? offer.description_ar : offer.description_en;
  const remaining = offer.max_clients > 0 ? offer.max_clients - (offer.claimed_count || 0) : null;

  return (
    <div className={`rounded-2xl overflow-hidden border transition-all ${expired ? "border-gray-700 opacity-60" : "border-red-400/30 hover:border-red-400/60 hover:shadow-xl hover:shadow-red-400/5"}`} data-testid={`offer-${offer.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 p-4 relative">
        {offer.discount_percent > 0 && (
          <div className="absolute top-3 left-3 bg-white text-red-500 font-extrabold text-lg px-3 py-1 rounded-lg">{offer.discount_percent}% {t.offers.off}</div>
        )}
        <h3 className="font-bold text-lg text-white pr-2">{title || (isRTL ? "عرض خاص" : "Special Offer")}</h3>
        {desc && <p className="text-white/80 text-sm mt-1">{desc}</p>}
      </div>

      <div className="bg-[#0d1220] p-5 space-y-4">
        {/* Price */}
        {(offer.original_price > 0 || offer.offer_price > 0) && (
          <div className="flex items-baseline gap-3">
            {offer.original_price > 0 && <span className="text-gray-500 line-through text-sm">{Number(offer.original_price).toLocaleString()} {isRTL ? "د.ع" : "IQD"}</span>}
            {offer.offer_price > 0 && <span className="text-amber-400 font-extrabold text-xl">{Number(offer.offer_price).toLocaleString()} {isRTL ? "د.ع" : "IQD"}</span>}
          </div>
        )}

        {/* Countdown */}
        {offer.end_date && !expired && (
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { val: timeLeft.days, label: t.offers.days },
              { val: timeLeft.hours, label: t.offers.hours },
              { val: timeLeft.min, label: t.offers.min },
              { val: timeLeft.sec, label: t.offers.sec }
            ].map((u, i) => (
              <div key={i} className="bg-white/5 rounded-lg p-2">
                <p className="text-xl font-bold text-white">{String(u.val || 0).padStart(2, "0")}</p>
                <p className="text-[10px] text-gray-500">{u.label}</p>
              </div>
            ))}
          </div>
        )}
        {expired && <p className="text-center text-red-400 font-bold text-sm">{t.offers.expired}</p>}

        {/* Remaining spots */}
        {remaining !== null && remaining > 0 && !expired && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">{offer.claimed_count || 0} {t.offers.claimed}</span>
            <span className="text-red-400 font-bold">{remaining} {t.offers.remaining}</span>
          </div>
        )}

        {/* CTA */}
        {!expired && (
          <button onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })} className="w-full py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-xl hover:opacity-90 transition-opacity text-sm">
            {t.offers.bookNow}
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function WebsitePage() {
  const [lang, setLang] = useState("ar");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: "", phone: "", service: "", date: "", notes: "" });
  const [bookingStatus, setBookingStatus] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [cmsData, setCmsData] = useState({});
  const [galleryItems, setGalleryItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [offers, setOffers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [photoFilter, setPhotoFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [instaPosts, setInstaPosts] = useState([]);
  const t = translations[lang];
  const isRTL = lang === "ar";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    (async () => {
      try { const res = await fetch(`${API_BASE}/api/website/content`); if (res.ok) setCmsData(await res.json()); } catch (e) { console.error("Failed to load CMS content:", e); }
      try { const res = await fetch(`${API_BASE}/api/website/gallery`); if (res.ok) setGalleryItems(await res.json()); } catch (e) { console.error("Failed to load gallery:", e); }
      try { const res = await fetch(`${API_BASE}/api/website/photos`); if (res.ok) setPhotos(await res.json()); } catch (e) { console.error("Failed to load photos:", e); }
      try { const res = await fetch(`${API_BASE}/api/offers?active_only=true`); if (res.ok) setOffers(await res.json()); } catch (e) { console.error("Failed to load offers:", e); }
      try { const res = await fetch(`${API_BASE}/api/reviews?approved_only=true`); if (res.ok) setReviews(await res.json()); } catch (e) { console.error("Failed to load reviews:", e); }
      try { const res = await fetch(`${API_BASE}/api/instagram/feed`); if (res.ok) setInstaPosts(await res.json()); } catch (e) { console.error("Failed to load Instagram feed:", e); }
    })();
  }, []);

  useEffect(() => {
    document.title = lang === "ar" ? "المركز الوطني التركي لزراعة الشعر والتجميل | Vatan Hair" : "Vatan Hair | Hair Transplant & Cosmetics Center";
  }, [lang]);

  const cms = (section, key, fallback) => cmsData[section]?.[key] || fallback;
  const cmsItems = (section, fallback) => cmsData[section]?.items?.length > 0 ? cmsData[section].items : fallback;

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMobileMenu(false); };

  const handleBooking = async (e) => {
    e.preventDefault(); setBookingLoading(true); setBookingStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/booking`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...bookingForm, language: lang }) });
      if (res.ok) { setBookingStatus("success"); setBookingForm({ name: "", phone: "", service: "", date: "", notes: "" }); } else setBookingStatus("error");
    } catch (err) { console.error("Booking error:", err); setBookingStatus("error"); } finally { setBookingLoading(false); }
  };

  const navItems = [
    { id: "hero", label: t.nav.home }, { id: "about", label: t.nav.about }, { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery }, { id: "offers", label: t.nav.offers }, { id: "hair-analysis", label: t.nav.hairAnalysis }, { id: "testimonials", label: t.nav.testimonials },
    { id: "booking", label: t.nav.booking }, { id: "contact", label: t.nav.contact }
  ];
  const servicesList = lang === "ar" ? ["زراعة الشعر","زراعة اللحية","زراعة الحاجب","جلسة نضارة","بلازما الشعر","ميزوثيرابي"] : ["Hair Transplant","Beard Transplant","Eyebrow Transplant","Freshness Session","Hair Plasma","Mesotherapy"];

  return (
    <div className={`min-h-screen bg-[#0a0f1a] text-white`} dir={isRTL ? "rtl" : "ltr"} style={{ fontFamily: isRTL ? "'Tajawal',sans-serif" : "'Inter',sans-serif" }}>

      {/* ===== NAVBAR ===== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0a0f1a]/95 backdrop-blur-md shadow-lg" : "bg-transparent"}`} data-testid="website-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollTo("hero")}>
              <img src={LOGO_URL} alt="Vatan Hair" className="h-10 md:h-12 w-10 md:w-12 rounded-lg object-cover" />
              <span className="font-bold text-white text-sm md:text-base hidden sm:block">Vatan Hair</span>
            </div>
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map(item => (
                <button key={item.id} onClick={() => scrollTo(item.id)} className="px-3 py-2 text-sm text-gray-300 hover:text-amber-400 transition-colors">{item.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="px-3 py-1.5 text-xs font-bold border border-amber-400 text-amber-400 rounded-full hover:bg-amber-400 hover:text-gray-900 transition-all" data-testid="lang-toggle">{lang === "ar" ? "EN" : "عربي"}</button>
              <a href={`tel:${cms("contact","phone1","07723220220")}`} className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full border border-gray-600 text-gray-300 hover:border-amber-400 hover:text-amber-400 transition-colors"><Phone className="w-4 h-4" /></a>
              <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-2 text-white" data-testid="mobile-menu-btn">{mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
            </div>
          </div>
        </div>
        {mobileMenu && (
          <div className="lg:hidden bg-[#0a0f1a]/98 backdrop-blur-lg border-t border-gray-800">
            <div className="px-4 py-3 space-y-1">
              {navItems.map(item => (<button key={item.id} onClick={() => scrollTo(item.id)} className="block w-full text-right px-4 py-3 text-sm text-gray-300 hover:text-amber-400 hover:bg-white/5 rounded-lg">{item.label}</button>))}
            </div>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section id="hero" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`text-center lg:${isRTL ? "text-right" : "text-left"} space-y-6`}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 rounded-full border border-amber-400/20">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">{cms("hero", isRTL ? "badge_ar" : "badge_en", t.hero.badge)}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
                {cms("hero", isRTL ? "title_ar" : "title_en", t.hero.title)}
                <span className="block text-amber-400 mt-2">{cms("hero", isRTL ? "subtitle_ar" : "subtitle_en", t.hero.subtitle)}</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto lg:mx-0">{cms("hero", isRTL ? "desc_ar" : "desc_en", t.hero.desc)}</p>
              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0">
                {t.hero.badges.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
                    <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" /><span className="text-xs text-gray-300">{b}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button onClick={() => scrollTo("booking")} className="group px-8 py-4 bg-amber-400 text-gray-900 font-bold rounded-full hover:bg-amber-300 transition-all flex items-center justify-center gap-2" data-testid="hero-cta-btn">
                  {t.hero.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                </button>
                <a href={`https://wa.me/${cms("contact","whatsapp","96407723220220")}`} target="_blank" rel="noopener noreferrer" className="px-8 py-4 border border-gray-600 text-white font-bold rounded-full hover:border-green-500 hover:text-green-400 transition-all flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> {t.hero.cta2}
                </a>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-80 h-96 rounded-2xl overflow-hidden shadow-2xl shadow-amber-400/10 border border-white/10">
                  <img src={imgUrl(cms("hero","hero_image",LOGO_URL))} alt="Vatan Hair" className="w-full h-full object-cover" />
                </div>
                <div className={`absolute -bottom-4 ${isRTL ? "-left-4" : "-right-4"} bg-[#1a1f2e] px-5 py-3 rounded-xl border border-amber-400/30 shadow-xl`}>
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2 rtl:space-x-reverse">
                      {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 border-2 border-[#1a1f2e] flex items-center justify-center text-xs font-bold text-gray-900">{i}</div>)}
                    </div>
                    <div>
                      <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}</div>
                      <span className="text-xs text-gray-400">+5000 {isRTL ? "مريض سعيد" : "Happy Patients"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="py-20 lg:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img src={imgUrl(cms("hero","about_image","https://images.pexels.com/photos/27138343/pexels-photo-27138343.jpeg?auto=compress&cs=tinysrgb&w=800"))} alt="Clinic" className="w-full h-96 object-cover" />
              </div>
              <div className={`absolute -top-4 ${isRTL ? "-left-4" : "-right-4"} bg-[#1a1f2e] px-5 py-3 rounded-xl border border-amber-400/30`}>
                <div className="flex items-center gap-2"><Award className="w-5 h-5 text-amber-400" /><span className="font-bold text-sm">{isRTL ? "مستشار زراعة الشعر" : "Hair Transplant Expert"}</span></div>
              </div>
            </div>
            <div className="space-y-6">
              <span className="text-amber-400 text-sm font-bold">{t.about.subtitle}</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold">{cms("hero", isRTL ? "title_ar" : "title_en", t.about.title)} <span className="text-amber-400">{t.about.subtitle}</span></h2>
              <p className="text-gray-400 leading-relaxed">{cms("hero", isRTL ? "about_ar" : "about_en", t.about.p1)}</p>
              <div className="space-y-4">
                {t.about.features.map((f, i) => {
                  const Icon = aboutIcons[i];
                  return (
                    <div key={i} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-amber-400/20 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-amber-400" /></div>
                      <div><h4 className="font-bold text-sm mb-1">{f.title}</h4><p className="text-xs text-gray-500">{f.desc}</p></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {t.stats.items.map((s, i) => {
              const Icon = statIcons[i];
              return (
                <div key={i} className="p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-amber-400/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-amber-400" /></div>
                  <p className="text-3xl sm:text-4xl font-extrabold text-amber-400">{s.num}</p>
                  <p className="text-sm font-bold text-white mt-1">{s.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-20 lg:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-amber-400 text-sm font-bold">{t.services.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.services.title}</h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">{t.services.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(() => {
              const c = cmsItems("services", null);
              const svcs = c ? c.map(s => ({ title: isRTL ? s.title_ar : s.title_en, desc: isRTL ? s.desc_ar : s.desc_en, image: s.image })) : t.services.items;
              return svcs.map((svc, i) => {
                const Icon = serviceIcons[i % serviceIcons.length];
                return (
                  <div key={i} className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-amber-400/30 transition-all" data-testid={`service-card-${i}`}>
                    {svc.image ? <div className="w-14 h-14 rounded-xl overflow-hidden mb-4"><img src={imgUrl(svc.image)} alt="" className="w-full h-full object-cover" /></div>
                    : <div className="w-14 h-14 rounded-xl bg-amber-400/10 flex items-center justify-center mb-4"><Icon className="w-7 h-7 text-amber-400" /></div>}
                    <h3 className="font-bold text-lg mb-2">{svc.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ===== OFFERS / DEALS ===== */}
      {offers.length > 0 && (
        <section id="offers" className="py-20 lg:py-28 bg-gradient-to-b from-[#0a0f1a] to-[#12091a]" data-testid="offers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <span className="text-red-400 text-sm font-bold animate-pulse">{t.offers.tag}</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.offers.title}</h2>
              <p className="text-gray-400 mt-3">{t.offers.subtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.filter(o => o.is_active).map((offer) => (
                <OfferCard key={offer.id} offer={offer} lang={lang} t={t} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== GALLERY (Before/After - Professional Layout) ===== */}
      <section id="gallery" className="py-20 lg:py-28 bg-[#0d1220]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-amber-400 text-sm font-bold">{t.gallery.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.gallery.title}</h2>
            <p className="text-gray-400 mt-3">{t.gallery.subtitle}</p>
          </div>

          {(() => {
            const items = galleryItems.length > 0 ? galleryItems : [
              { id: "demo-1", title_ar: "زراعة شعر - تقنية FUE", title_en: "Hair Transplant - FUE", grafts: "3500", duration_ar: "بعد 8 أشهر", duration_en: "After 8 months", patient_ar: "أحمد م.", patient_en: "Ahmed M." },
              { id: "demo-2", title_ar: "زراعة شعر أمامي", title_en: "Frontal Hair Transplant", grafts: "2800", duration_ar: "بعد 10 أشهر", duration_en: "After 10 months", patient_ar: "علي ح.", patient_en: "Ali H." },
              { id: "demo-3", title_ar: "زراعة شعر كامل", title_en: "Full Hair Transplant", grafts: "4200", duration_ar: "بعد 12 شهر", duration_en: "After 12 months", patient_ar: "محمد ع.", patient_en: "Mohammed A." },
              { id: "demo-4", title_ar: "زراعة لحية", title_en: "Beard Transplant", grafts: "1800", duration_ar: "بعد 6 أشهر", duration_en: "After 6 months", patient_ar: "حسين ر.", patient_en: "Hussein R." },
              { id: "demo-5", title_ar: "تكثيف شعر", title_en: "Hair Densification", grafts: "2200", duration_ar: "بعد 9 أشهر", duration_en: "After 9 months", patient_ar: "عمر س.", patient_en: "Omar S." },
              { id: "demo-6", title_ar: "زراعة شعر - تقنية Sapphire", title_en: "Sapphire FUE", grafts: "3000", duration_ar: "بعد 10 أشهر", duration_en: "After 10 months", patient_ar: "كرار ف.", patient_en: "Karar F." }
            ];
            const featured = items[0];
            const gridItems = items.slice(0, 6);
            const title = (item) => isRTL ? (item.title_ar || "زراعة شعر") : (item.title_en || "Hair Transplant");
            const patient = (item) => isRTL ? (item.patient_ar || "") : (item.patient_en || "");
            const duration = (item) => isRTL ? (item.duration_ar || "") : (item.duration_en || "");

            return (
              <>
                {/* Featured Slider - Hero */}
                <div className="max-w-3xl mx-auto mb-16">
                  <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-2xl" data-testid="gallery-featured">
                    <BeforeAfterSlider before={imgUrl(featured.before_image)} after={imgUrl(featured.after_image)} labelBefore={t.gallery.before} labelAfter={t.gallery.after} />
                    <div className="p-5 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h3 className="font-bold text-lg text-white">{title(featured)}</h3>
                        <p className="text-gray-500 text-sm">{patient(featured)}</p>
                      </div>
                      <div className="flex gap-3">
                        {featured.grafts && <span className="px-3 py-1.5 bg-amber-400/10 text-amber-400 text-xs font-bold rounded-full">{featured.grafts} {isRTL ? "بصيلة" : "grafts"}</span>}
                        {duration(featured) && <span className="px-3 py-1.5 bg-emerald-400/10 text-emerald-400 text-xs font-bold rounded-full">{duration(featured)}</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cases Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {gridItems.map((item, idx) => {
                    const hasImages = item.before_image || item.after_image;
                    return (
                      <div key={item.id || idx} className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-amber-400/30 transition-all hover:shadow-xl hover:shadow-amber-400/5" data-testid={`gallery-item-${idx}`}>
                        {/* Image Comparison */}
                        <div className="relative">
                          <BeforeAfterSlider before={imgUrl(item.before_image)} after={imgUrl(item.after_image)} labelBefore={t.gallery.before} labelAfter={t.gallery.after} />
                          {/* Overlay badges */}
                          {item.grafts && (
                            <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center z-20 pointer-events-none">
                              <span className="px-2.5 py-1 bg-black/70 backdrop-blur-sm text-amber-400 text-xs font-bold rounded-lg">{item.grafts} {isRTL ? "بصيلة" : "grafts"}</span>
                              {duration(item) && <span className="px-2.5 py-1 bg-black/70 backdrop-blur-sm text-emerald-400 text-xs font-bold rounded-lg">{duration(item)}</span>}
                            </div>
                          )}
                        </div>
                        {/* Case Details */}
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-bold text-sm text-white">{title(item)}</h4>
                              {patient(item) && <p className="text-gray-500 text-xs mt-0.5">{patient(item)}</p>}
                            </div>
                            <div className="w-8 h-8 rounded-full bg-amber-400/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Eye className="w-4 h-4 text-amber-400" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Carousel - Quick Scroll */}
                {items.length > 3 && (
                  <div className="relative">
                    <h3 className="text-lg font-bold text-center mb-6 text-gray-300">{isRTL ? "المزيد من النتائج" : "More Results"}</h3>
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" data-testid="gallery-carousel">
                      {items.map((item, idx) => (
                        <div key={`carousel-${item.id || idx}`} className="snap-center shrink-0 w-64 rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-amber-400/30 transition-all">
                          <div className="h-40 relative">
                            {item.after_image ? (
                              <img src={imgUrl(item.after_image)} alt="" className="w-full h-full object-cover" />
                            ) : item.before_image ? (
                              <img src={imgUrl(item.before_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-amber-400/10 to-transparent flex items-center justify-center"><Users className="w-10 h-10 text-gray-600" /></div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute bottom-2 left-2 right-2">
                              <p className="text-white text-xs font-bold truncate">{title(item)}</p>
                              <div className="flex gap-2 mt-1">
                                {item.grafts && <span className="text-amber-400 text-[10px]">{item.grafts} {isRTL ? "بصيلة" : "grafts"}</span>}
                                {duration(item) && <span className="text-emerald-400 text-[10px]">{duration(item)}</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {galleryItems.length === 0 && <p className="text-center mt-6 text-gray-600 text-sm">{isRTL ? "سيتم إضافة صور حقيقية قريباً - يمكنك رفعها من إدارة الموقع" : "Real photos coming soon - upload via CMS"}</p>}
        </div>
      </section>

      {/* ===== PHOTO GALLERY ===== */}
      <section id="photos" className="py-20 lg:py-28" data-testid="photos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-amber-400 text-sm font-bold">{isRTL ? "معرض الصور" : "Photo Gallery"}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{isRTL ? "صور من المركز" : "Photos from Our Center"}</h2>
          </div>
          {/* Category Filter */}
          {photos.length > 0 && (
            <div className="flex justify-center gap-2 mb-8 flex-wrap">
              {[
                { value: "all", ar: "الكل", en: "All" },
                { value: "results", ar: "نتائج العمليات", en: "Results" },
                { value: "clinic", ar: "المركز", en: "Clinic" },
                { value: "team", ar: "الفريق", en: "Team" },
                { value: "general", ar: "عام", en: "General" },
              ].map(cat => {
                const count = cat.value === "all" ? photos.length : photos.filter(p => p.category === cat.value).length;
                if (cat.value !== "all" && count === 0) return null;
                return (
                  <button key={cat.value} onClick={() => setPhotoFilter(cat.value)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${photoFilter === cat.value ? "bg-amber-400 text-gray-900" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}>
                    {isRTL ? cat.ar : cat.en}
                  </button>
                );
              })}
            </div>
          )}
          {/* Photos Grid */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(photoFilter === "all" ? photos : photos.filter(p => p.category === photoFilter)).map((photo, idx) => (
                <div key={photo.id} onClick={() => setLightbox(idx)} className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer border border-white/10 hover:border-amber-400/30 transition-all" data-testid={`photo-${idx}`}>
                  <img src={imgUrl(photo.image)} alt={isRTL ? photo.title_ar : photo.title_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white text-sm font-medium truncate">{isRTL ? photo.title_ar : photo.title_en}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500">{isRTL ? "سيتم إضافة الصور قريباً" : "Photos coming soon"}</p>
            </div>
          )}
        </div>
      </section>

      {/* ===== LIGHTBOX ===== */}
      {lightbox !== null && (() => {
        const filteredPhotos = photoFilter === "all" ? photos : photos.filter(p => p.category === photoFilter);
        const photo = filteredPhotos[lightbox];
        if (!photo) return null;
        return (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightbox(null)} data-testid="lightbox">
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 z-10"><X className="w-6 h-6" /></button>
            {lightbox > 0 && <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 z-10"><ChevronLeft className="w-6 h-6" /></button>}
            {lightbox < filteredPhotos.length - 1 && <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 z-10"><ChevronRight className="w-6 h-6" /></button>}
            <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[85vh] px-4">
              <img src={imgUrl(photo.image)} alt="" className="max-w-full max-h-[80vh] object-contain rounded-lg" />
              <p className="text-center text-gray-400 text-sm mt-3">{isRTL ? photo.title_ar : photo.title_en} <span className="text-gray-600">({lightbox + 1}/{filteredPhotos.length})</span></p>
            </div>
          </div>
        );
      })()}

      {/* ===== TESTIMONIALS (Real Reviews + CMS) ===== */}
      <section id="testimonials" className="py-20 lg:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-amber-400 text-sm font-bold">{reviews.length > 0 ? t.reviews.tag : t.testimonials.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{reviews.length > 0 ? t.reviews.title : t.testimonials.title}</h2>
            {reviews.length > 0 && <p className="text-gray-400 mt-3">{t.reviews.subtitle}</p>}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Real reviews from DB first */}
            {reviews.map((review, i) => (
              <div key={`review-${review.id}`} className="p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-amber-400/20 transition-colors" data-testid={`review-${i}`}>
                <div className="flex gap-1 mb-4">{[...Array(review.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}{[...Array(5 - (review.rating || 5))].map((_, j) => <Star key={j} className="w-4 h-4 text-gray-700" />)}</div>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-gray-900 font-bold text-sm">{(review.patient_name || "?").charAt(0)}</div>
                  <div>
                    <span className="font-bold text-sm block">{review.patient_name}</span>
                    {review.service && <span className="text-xs text-gray-500">{review.service}</span>}
                  </div>
                  <CheckCircle className="w-4 h-4 text-green-400 mr-auto" title={isRTL ? "عميل حقيقي" : "Verified"} />
                </div>
              </div>
            ))}
            {/* CMS testimonials as fallback */}
            {(() => {
              const c = cmsItems("testimonials", null);
              const items = c ? c.map(t => ({ name: isRTL ? t.name_ar : t.name_en, text: isRTL ? t.text_ar : t.text_en, rating: Number(t.rating)||5 })) : (reviews.length === 0 ? t.testimonials.items : []);
              return items.map((item, i) => (
                <div key={`cms-${i}`} className="p-6 bg-white/5 rounded-2xl border border-white/10" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-1 mb-4">{[...Array(item.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6">"{item.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-gray-900 font-bold text-sm">{item.name.charAt(0)}</div>
                    <span className="font-bold text-sm">{item.name}</span>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-20 lg:py-28 bg-[#0d1220]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-amber-400 text-sm font-bold">{t.faq.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {(() => {
              const c = cmsItems("faq", null);
              const items = c ? c.map(f => ({ q: isRTL ? f.q_ar : f.q_en, a: isRTL ? f.a_ar : f.a_en })) : t.faq.items;
              return items.map((item, i) => (
                <div key={i} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid={`faq-item-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors">
                    <span className="font-bold text-sm flex-1 text-right">{item.q}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 text-amber-400 shrink-0 mx-2" /> : <ChevronDown className="w-5 h-5 text-gray-500 shrink-0 mx-2" />}
                  </button>
                  {openFaq === i && <div className="px-5 pb-5"><p className="text-gray-400 text-sm leading-relaxed">{item.a}</p></div>}
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* ===== AI HAIR ANALYSIS CTA ===== */}
      <section id="hair-analysis" className="py-20 lg:py-28 bg-gradient-to-b from-[#0a0f1a] to-[#0d1a12]" data-testid="hair-analysis-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block px-4 py-1.5 bg-emerald-400/10 text-emerald-400 text-sm font-bold rounded-full mb-4">{t.hairAnalysis.badge}</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.hairAnalysis.title}</h2>
          <p className="text-gray-400 mt-4 max-w-xl mx-auto">{t.hairAnalysis.ctaDesc}</p>
          <div className="mt-10 bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 sm:p-12 max-w-lg mx-auto">
            <div className="w-20 h-20 mx-auto bg-emerald-400/10 rounded-full flex items-center justify-center mb-6">
              <Camera className="w-10 h-10 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold mb-3">{t.hairAnalysis.cta}</h3>
            <p className="text-gray-400 text-sm mb-6">{t.hairAnalysis.subtitle}</p>
            <button onClick={() => { document.querySelector('[data-testid="chat-toggle-btn"]')?.click(); setTimeout(() => document.querySelector('[data-testid="chat-analyze-btn"]')?.click(), 300); }} className="px-8 py-4 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-400 transition-all inline-flex items-center gap-2" data-testid="hair-analysis-cta-btn">
              <Camera className="w-5 h-5" />
              {t.hairAnalysis.upload}
            </button>
          </div>
        </div>
      </section>

      {/* ===== BOOKING ===== */}
      <section id="booking" className="py-20 lg:py-28" data-testid="booking-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-amber-400 text-sm font-bold">{t.booking.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.booking.title}</h2>
          </div>
          <form onSubmit={handleBooking} className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-white/10 space-y-5" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="block text-gray-400 text-sm mb-2">{t.booking.name} *</label><input type="text" required value={bookingForm.name} onChange={e => setBookingForm({...bookingForm, name: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-amber-400 focus:outline-none" data-testid="booking-name" /></div>
              <div><label className="block text-gray-400 text-sm mb-2">{t.booking.phone} *</label><input type="tel" required value={bookingForm.phone} onChange={e => setBookingForm({...bookingForm, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-amber-400 focus:outline-none" dir="ltr" data-testid="booking-phone" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="block text-gray-400 text-sm mb-2">{t.booking.service} *</label><select required value={bookingForm.service} onChange={e => setBookingForm({...bookingForm, service: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-amber-400 focus:outline-none" data-testid="booking-service"><option value="" className="bg-gray-900">{t.booking.selectService}</option>{servicesList.map((s) => <option key={s} value={s} className="bg-gray-900">{s}</option>)}</select></div>
              <div><label className="block text-gray-400 text-sm mb-2">{t.booking.date}</label><input type="date" value={bookingForm.date} onChange={e => setBookingForm({...bookingForm, date: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-amber-400 focus:outline-none" data-testid="booking-date" /></div>
            </div>
            {bookingStatus === "success" && <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3" data-testid="booking-success"><CheckCircle className="w-5 h-5 text-green-400" /><span className="text-green-400 text-sm">{t.booking.success}</span></div>}
            {bookingStatus === "error" && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" data-testid="booking-error">{t.booking.error}</div>}
            <button type="submit" disabled={bookingLoading} className="w-full py-4 bg-amber-400 text-gray-900 font-bold rounded-xl hover:bg-amber-300 transition-all disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-submit-btn">{bookingLoading ? t.booking.submitting : t.booking.submit} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></button>
          </form>
        </div>
      </section>

      {/* ===== INSTAGRAM FEED ===== */}
      {instaPosts.length > 0 && (
        <section id="instagram" className="py-20 lg:py-28 bg-[#0d1220]" data-testid="instagram-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <span className="text-pink-400 text-sm font-bold">{t.instagram.tag}</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold mt-2 bg-gradient-to-r from-pink-400 via-purple-400 to-amber-400 bg-clip-text text-transparent">{t.instagram.title}</h2>
              <p className="text-gray-400 mt-3">{t.instagram.subtitle}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {instaPosts.map((post, i) => (
                <a
                  key={post.id || i}
                  href={post.instagram_url || "#"}
                  target={post.instagram_url ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-pink-400/30 transition-all"
                  data-testid={`insta-post-${i}`}
                >
                  <img src={imgUrl(post.image_url)} alt={isRTL ? post.caption_ar : post.caption_en} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                    <p className="text-white text-sm line-clamp-2 mb-2">{isRTL ? post.caption_ar : post.caption_en}</p>
                    {post.likes > 0 && (
                      <div className="flex items-center gap-1 text-xs text-pink-300">
                        <Heart className="w-3.5 h-3.5 fill-pink-400 text-pink-400" />
                        <span>{post.likes} {t.instagram.likes}</span>
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center mt-10">
              <a
                href={cms("contact","instagram","https://www.instagram.com/vatanhair19/")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl font-bold hover:opacity-90 transition-opacity"
                data-testid="follow-instagram-btn"
              >
                <Instagram className="w-5 h-5" />
                {t.instagram.follow}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-20 lg:py-28 bg-[#0d1220]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-amber-400 text-sm font-bold">{t.contact.tag}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2">{t.contact.title}</h2>
            <p className="text-gray-400 mt-3">{t.contact.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Phone, label: t.contact.phone_label, value: cms("contact","phone1","07723220220"), sub: cms("contact","phone2","078220220220"), color: "text-amber-400", link: `tel:${cms("contact","phone1","07723220220")}` },
              { icon: Mail, label: t.contact.email_label, value: "jawzalabbas@gmail.com", sub: isRTL ? "للاستفسارات" : "For inquiries", color: "text-amber-400", link: "mailto:jawzalabbas@gmail.com" },
              { icon: MapPin, label: t.contact.address_label, value: cms("contact", isRTL ? "address_ar" : "address_en", t.contact.address), sub: cms("contact", isRTL ? "hours_ar" : "hours_en", t.contact.hours), color: "text-amber-400", link: "https://maps.google.com/?q=33.3152,44.3661" }
            ].map((c, i) => (
              <a key={i} href={c.link} target={i === 2 ? "_blank" : undefined} rel={i === 2 ? "noopener noreferrer" : undefined} className="text-center p-8 bg-white/5 rounded-2xl border border-white/10 hover:border-amber-400/30 transition-colors block cursor-pointer" data-testid={`contact-card-${i}`}>
                <div className="w-14 h-14 rounded-full bg-amber-400/10 flex items-center justify-center mx-auto mb-4"><c.icon className={`w-6 h-6 ${c.color}`} /></div>
                <h4 className="font-bold text-lg mb-2">{c.label}</h4>
                <p className={`${c.color} text-sm font-medium`} dir={i === 0 ? "ltr" : undefined}>{c.value}</p>
                <p className="text-xs text-gray-500 mt-1" dir={i === 0 ? "ltr" : undefined}>{c.sub}</p>
              </a>
            ))}
          </div>

          {/* Google Maps */}
          <div className="mb-10 rounded-2xl overflow-hidden border border-white/10" data-testid="google-map">
            <a href="https://maps.google.com/maps?q=%D8%A7%D9%84%D9%85%D8%B1%D9%83%D8%B2+%D8%A7%D9%84%D9%88%D8%B7%D9%86%D9%8A+%D8%A7%D9%84%D8%AA%D8%B1%D9%83%D9%8A+Vatan+Hair" target="_blank" rel="noopener noreferrer" className="block relative group">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3334.0543152959885!2d44.34725222548644!3d33.31739545617659!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15577faeb0b9d62f%3A0x7606091fa94541f4!2z2KfZhNmF2LHZg9iyINin2YTZiNi32YbZiiDYp9mE2KrYsdmD2Yog2YTYstix2KfYudipINin2YTYtNi52LEg2YjYp9mE2KrYrNmF2YrZhCBWYXRhbiBIYWly!5e0!3m2!1sar!2siq!4v1774378564218!5m2!1sar!2siq"
                width="100%" height="350" style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)" }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Vatan Hair Location"
              />
              <div className="absolute inset-0 bg-transparent group-hover:bg-amber-400/5 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-amber-400 text-gray-900 px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {isRTL ? "افتح في خرائط Google" : "Open in Google Maps"}
                </div>
              </div>
            </a>
          </div>

          <div className="flex justify-center gap-4">
            <a href={cms("contact","instagram","https://www.instagram.com/vatanhair19/")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl hover:opacity-90 transition-opacity"><Instagram className="w-5 h-5" /> Instagram</a>
            <a href={cms("contact","facebook","https://www.facebook.com/VatanHair19/")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-blue-600 rounded-xl hover:opacity-90 transition-opacity"><Facebook className="w-5 h-5" /> Facebook</a>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-8 border-t border-white/10" data-testid="website-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="" className="w-8 h-8 rounded-lg" /><span className="text-sm text-gray-400">{t.footer.brand}</span></div>
          <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} Vatan Hair. {t.footer.rights}.</p>
        </div>
      </footer>

      {/* ===== FLOATING BUTTONS ===== */}
      <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-3">
        <a href={`https://wa.me/${cms("contact","whatsapp","96407723220220")}`} target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 hover:bg-green-400 hover:scale-110 transition-all" data-testid="floating-whatsapp"><MessageCircle className="w-7 h-7 text-white" /></a>
        <a href={`tel:${cms("contact","phone1","07723220220")}`} className="w-14 h-14 bg-amber-400 rounded-full flex items-center justify-center shadow-lg shadow-amber-400/30 hover:bg-amber-300 hover:scale-110 transition-all" data-testid="floating-phone"><Phone className="w-7 h-7 text-gray-900" /></a>
      </div>

      {/* ===== AI CHAT WIDGET ===== */}
      <ChatWidget lang={lang} cms={cms} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        html { scroll-behavior: smooth; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
